import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Euro,
  ExternalLink,
  Hourglass,
  Info,
  Loader2,
  Lock,
  LogIn,
  MapPin,
  Plus,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import entryService, {
  CustomEntryField,
  CustomEntryInput,
  RegistrationConfig,
  RegistrationPageData,
  TeamPlayerPayload,
} from '../services/entryService';

const ACCENT = '#3fced1';

// ---------------------------------------------------------------------
// Form-Datentypen
// ---------------------------------------------------------------------

interface PlayerFormData {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  birthday: string; // YYYY-MM-DD (Input-Format)
  customValues: Record<string, any>;
}

function emptyPlayer(): PlayerFormData {
  return { firstname: '', lastname: '', email: '', phone: '', birthday: '', customValues: {} };
}

/** Custom-Field-Wert in den String konvertieren, den das Backend erwartet. */
function customValueToString(field: CustomEntryField, value: any): string | null {
  if (value === null || value === undefined || value === '') return null;
  const type = (field.type || 'text').toLowerCase();
  if (type === 'checkbox') return value === true || value === 'true' ? 'true' : 'false';
  if (type === 'date') {
    // Wie Flutter: Millisekunden-Timestamp als String
    const ts = entryService.inputValueToBirthday(value.toString());
    return ts != null ? ts.toString() : null;
  }
  return value.toString();
}

function buildCustomInputs(fields: CustomEntryField[], values: Record<string, any>): CustomEntryInput[] {
  const inputs: CustomEntryInput[] = [];
  for (const field of fields) {
    if (!field.id) continue;
    inputs.push({ id: field.id, value: customValueToString(field, values[field.id]) });
  }
  return inputs;
}

// ---------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------

const TournamentRegistrationPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pageData, setPageData] = useState<RegistrationPageData | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  // Formular
  const [players, setPlayers] = useState<PlayerFormData[]>([emptyPlayer()]);
  const [teamName, setTeamName] = useState('');
  const [teamCustomValues, setTeamCustomValues] = useState<Record<string, any>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Duplicate-Check
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);

  // Erfolg
  const [successState, setSuccessState] = useState<'NONE' | 'REGISTERED' | 'WAITLIST'>('NONE');

  const tournament: RegistrationConfig | null = pageData?.tournament || null;

  // ---------------- Abgeleitete Werte ----------------

  const tournamentType = (tournament?.type || 'SINGLE').toString().toUpperCase();
  const isSingle = tournamentType === 'SINGLE';
  const isDouble = tournamentType === 'DOUBLE';
  const isTeam = tournamentType === 'TEAM';

  const minPlayers = isSingle ? 1 : isDouble ? 2 : Math.max(1, tournament?.teamSize ?? 2);
  const maxPlayers = isSingle
    ? 1
    : isDouble
      ? 2
      : Math.max(minPlayers, tournament?.teamSizeMax ?? tournament?.teamSize ?? 4);

  const emailRequired = tournament?.entryEmailRequired === true;
  const phoneRequired = tournament?.entryPhoneRequired === true;
  const birthdayRequired = tournament?.entryBirthdayRequired === true;

  const sortedFields = useMemo(
    () => entryService.sortCustomFields(tournament?.customEntryFields),
    [tournament]
  );
  const teamFields = useMemo(() => sortedFields.filter((f) => f.perPlayer !== true), [sortedFields]);
  const playerFields = useMemo(() => sortedFields.filter((f) => f.perPlayer === true), [sortedFields]);

  const deadlinePassed = useMemo(() => {
    if (!tournament?.entryDeadline || typeof tournament.entryDeadline !== 'number') return false;
    return Date.now() > tournament.entryDeadline;
  }, [tournament]);

  const registrationClosed = tournament?.entryEnabled === false || deadlinePassed;

  // ---------------- Laden ----------------

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await entryService.getRegistrationData(slug);
      if (!data) {
        setLoadError('Anmeldedaten konnten nicht geladen werden.');
        return;
      }
      setPageData(data);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        setLoadError('Dieses Turnier existiert nicht.');
      } else {
        setLoadError(entryService.getErrorMessage(err, 'Anmeldung für dieses Turnier nicht verfügbar.'));
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  // Bereits angemeldet?
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!isAuthenticated || !tournament?.id) return;
      try {
        const regs = await entryService.getUserRegistrations();
        const all = [...(regs.upcoming || []), ...(regs.past || [])];
        const found = all.some((r) => r?.registerable?.id && r.registerable.id === tournament.id);
        if (!cancelled) setAlreadyRegistered(found);
      } catch {
        /* still erlauben – Backend prüft beim Submit erneut */
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tournament?.id]);

  // Spieler initialisieren + Vorbefüllung aus eingeloggtem User
  useEffect(() => {
    if (!tournament) return;
    setPlayers((prev) => {
      const next: PlayerFormData[] = [];
      for (let i = 0; i < minPlayers; i++) {
        next.push(prev[i] ? { ...prev[i] } : emptyPlayer());
      }
      if (next.length > 0 && user) {
        const p0 = next[0];
        if (!p0.firstname) p0.firstname = user.firstname || (user as any)?.personalData?.firstname || '';
        if (!p0.lastname) p0.lastname = user.lastname || (user as any)?.personalData?.lastname || '';
        if (!p0.email) p0.email = user.email || (user as any)?.personalData?.email || '';
        if (!p0.phone) p0.phone = (user as any)?.personalData?.phone || '';
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.id, minPlayers, user?.id]);

  // ---------------- Form-Handler ----------------

  const updatePlayer = (index: number, patch: Partial<PlayerFormData>) => {
    setPlayers((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    setDuplicateWarning(null);
    setDuplicateConfirmed(false);
  };

  const updatePlayerCustomValue = (index: number, fieldId: string, value: any) => {
    setPlayers((prev) =>
      prev.map((p, i) => (i === index ? { ...p, customValues: { ...p.customValues, [fieldId]: value } } : p))
    );
  };

  const addPlayer = () => {
    if (players.length < maxPlayers) setPlayers((prev) => [...prev, emptyPlayer()]);
  };

  const removePlayer = (index: number) => {
    if (index === 0 || players.length <= minPlayers) return;
    setPlayers((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------- Validierung ----------------

  const validate = (): string | null => {
    for (let i = 0; i < players.length; i++) {
      const p = players[i];
      const label = isSingle ? '' : ` (Spieler ${i + 1})`;
      if (!p.firstname.trim()) return `Bitte gib den Vornamen an${label}.`;
      if (!p.lastname.trim()) return `Bitte gib den Nachnamen an${label}.`;
      if (emailRequired && !p.email.trim()) return `Bitte gib die E-Mail-Adresse an${label}.`;
      if (p.email.trim() && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(p.email.trim())) {
        return `Ungültige E-Mail-Adresse${label}.`;
      }
      if (phoneRequired && !p.phone.trim()) return `Bitte gib die Telefonnummer an${label}.`;
      if (birthdayRequired && !p.birthday) return `Bitte gib das Geburtsdatum an${label}.`;

      for (const field of playerFields) {
        const err = entryService.validateCustomField(field, p.customValues[field.id || '']);
        if (err) return `${err}${label}`;
      }
    }

    for (const field of teamFields) {
      const err = entryService.validateCustomField(field, teamCustomValues[field.id || '']);
      if (err) return err;
    }

    return null;
  };

  // ---------------- Submit ----------------

  const handleSubmit = async () => {
    if (!tournament?.id) return;
    setFormError(null);

    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      // Duplicate-Check vor Submit (nur einmal warnen, danach „Trotzdem anmelden")
      if (!duplicateConfirmed) {
        try {
          if (isSingle) {
            const result = await entryService.checkDuplicate(
              tournament.id,
              players[0].firstname.trim(),
              players[0].lastname.trim(),
              players[0].email.trim() || null
            );
            if (result?.isDuplicate === true) {
              setDuplicateWarning(
                result.message || 'Ein Teilnehmer mit diesen Daten ist bereits angemeldet.'
              );
              setSubmitting(false);
              return;
            }
          } else {
            const result = await entryService.checkTeamDuplicate(
              tournament.id,
              players.map((p) => ({
                firstname: p.firstname.trim(),
                lastname: p.lastname.trim(),
                email: p.email.trim() || null,
              }))
            );
            if (result?.hasDuplicates === true) {
              const msgs = (result.duplicates || [])
                .map((d) => d?.message)
                .filter(Boolean)
                .join(' ');
              setDuplicateWarning(msgs || 'Mindestens ein Spieler ist bereits angemeldet.');
              setSubmitting(false);
              return;
            }
          }
        } catch {
          /* Duplicate-Check optional – bei Fehler trotzdem fortfahren */
        }
      }

      if (isSingle) {
        const p = players[0];
        const allInputs = [
          ...buildCustomInputs(teamFields, teamCustomValues),
          ...buildCustomInputs(playerFields, p.customValues),
        ];
        const entry = await entryService.enterTournament(tournament.id, {
          firstname: p.firstname.trim(),
          lastname: p.lastname.trim(),
          email: p.email.trim() || user?.email || null,
          phone: p.phone.trim() || null,
          birthday: entryService.inputValueToBirthday(p.birthday),
          customEntryInputs: allInputs.length > 0 ? allInputs : undefined,
        });
        const isWaiting = entry?.waiting === true || entryService.getEffectiveStatus(entry) === 'WAITLIST';
        setSuccessState(isWaiting ? 'WAITLIST' : 'REGISTERED');
      } else {
        const teamPlayers: TeamPlayerPayload[] = players.map((p, i) => ({
          firstname: p.firstname.trim(),
          lastname: p.lastname.trim(),
          email: p.email.trim() || null,
          phone: p.phone.trim() || null,
          birthday: entryService.inputValueToBirthday(p.birthday),
          // Erster Spieler wird mit dem eingeloggten User verknüpft (wie Flutter)
          username: i === 0 ? (user as any)?.username || null : null,
          customEntryInputs: buildCustomInputs(playerFields, p.customValues),
        }));
        const entry = await entryService.enterTeam(tournament.id, {
          teamName: teamName.trim() || null,
          players: teamPlayers,
          customEntryInputs: buildCustomInputs(teamFields, teamCustomValues),
        });
        const isWaiting = entry?.waiting === true || entryService.getEffectiveStatus(entry) === 'WAITLIST';
        setSuccessState(isWaiting ? 'WAITLIST' : 'REGISTERED');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setFormError(entryService.getErrorMessage(err, 'Anmeldung fehlgeschlagen.'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // ===================================================================
  // Render
  // ===================================================================

  const pageTitle = tournament?.name || pageData?.tournamentName || 'Turnier';

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20">
        <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        <p className="mt-4 text-sm text-gray-400">Anmeldung laden…</p>
      </div>
    );
  }

  // Fehler beim Laden (Turnier existiert nicht / keine Anmeldung verfügbar)
  if (loadError || !pageData) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20 text-center">
        <Helmet>
          <title>Anmeldung | DartCloud</title>
        </Helmet>
        <div className="h-16 w-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
          <AlertCircle size={32} className="text-gray-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Anmeldung nicht verfügbar</h1>
        <p className="text-sm text-gray-400 mb-6 max-w-md">{loadError || 'Für dieses Turnier ist keine Anmeldung verfügbar.'}</p>
        <button
          onClick={() => navigate(slug ? `/turnier/${slug}` : '/turniere')}
          className="px-4 py-2 rounded-lg bg-white text-black font-medium text-sm hover:bg-gray-200 transition"
        >
          Zurück zum Turnier
        </button>
      </div>
    );
  }

  // Externe Anmeldung
  if ((pageData.type || '').toString().toUpperCase() === 'EXTERNAL') {
    return (
      <div className="min-h-screen bg-black text-white px-4 py-12">
        <Helmet>
          <title>{`Anmeldung – ${pageTitle} | DartCloud`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-xl mx-auto">
          <BackLink slug={slug} />
          <div className="mt-6 p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${ACCENT}22` }}>
              <ExternalLink size={26} style={{ color: ACCENT }} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Externe Anmeldung</h1>
            <p className="text-sm text-gray-400 mb-6">
              Die Anmeldung für „{pageTitle}" erfolgt über eine externe Website.
            </p>
            {pageData.externalLink ? (
              <a
                href={pageData.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold text-black transition hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                <ExternalLink size={16} />
                Zur externen Anmeldung
              </a>
            ) : (
              <p className="text-sm text-gray-500">Kein externer Anmeldelink hinterlegt.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Erfolg
  if (successState !== 'NONE') {
    const isWaitlist = successState === 'WAITLIST';
    const paymentInfo = tournament?.paymentInfo;
    const showPayment =
      !isWaitlist &&
      typeof tournament?.entryFee === 'number' &&
      tournament.entryFee > 0 &&
      paymentInfo &&
      (paymentInfo.iban || paymentInfo.paypalLink);
    return (
      <div className="min-h-screen bg-black text-white px-4 py-12">
        <Helmet>
          <title>{`Anmeldung – ${pageTitle} | DartCloud`}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-xl mx-auto">
          <div className="p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div
              className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4"
              style={{ backgroundColor: isWaitlist ? '#F59E0B22' : '#10B98122' }}
            >
              {isWaitlist ? (
                <Hourglass size={26} className="text-amber-500" />
              ) : (
                <CheckCircle2 size={26} className="text-emerald-500" />
              )}
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {isWaitlist ? 'Auf der Warteliste' : 'Anmeldung erfolgreich!'}
            </h1>
            <p className="text-sm text-gray-400 mb-6">
              {isWaitlist
                ? `Das Turnier ist aktuell voll – du wurdest auf die Warteliste für „${pageTitle}" gesetzt. Du rückst automatisch nach, sobald ein Platz frei wird.`
                : `Du bist für „${pageTitle}" angemeldet.${tournament?.requireConfirmation === true ? ' Der Veranstalter bestätigt deine Anmeldung in Kürze.' : ''}`}
            </p>

            {showPayment && (
              <div className="text-left p-4 rounded-xl bg-zinc-800/70 border border-zinc-700 mb-6">
                <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                  <Euro size={16} style={{ color: ACCENT }} />
                  Zahlungsinformationen ({(tournament!.entryFee as number).toFixed(2).replace('.', ',')} €)
                </p>
                <div className="space-y-1 text-sm text-gray-300">
                  {paymentInfo?.accountHolder && <p>Kontoinhaber: {paymentInfo.accountHolder}</p>}
                  {paymentInfo?.iban && <p>IBAN: {paymentInfo.iban}</p>}
                  {paymentInfo?.bic && <p>BIC: {paymentInfo.bic}</p>}
                  {paymentInfo?.bankName && <p>Bank: {paymentInfo.bankName}</p>}
                  {paymentInfo?.paymentReference && <p>Verwendungszweck: {paymentInfo.paymentReference}</p>}
                  {paymentInfo?.additionalInfo && <p className="text-gray-400">{paymentInfo.additionalInfo}</p>}
                  {paymentInfo?.paypalLink && (
                    <a
                      href={paymentInfo.paypalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium"
                      style={{ color: ACCENT }}
                    >
                      <ExternalLink size={14} /> Per PayPal zahlen
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/meine-anmeldungen"
                className="flex-1 py-3 px-4 rounded-xl font-semibold text-black text-center transition hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                Meine Anmeldungen
              </Link>
              <Link
                to={`/turnier/${slug}`}
                className="flex-1 py-3 px-4 rounded-xl font-semibold border border-zinc-700 text-gray-200 text-center hover:bg-zinc-800 transition"
              >
                Zurück zum Turnier
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{`Anmeldung – ${pageTitle} | DartCloud`}</title>
        <meta
          name="description"
          content={`Jetzt für das Dart-Turnier ${pageTitle} anmelden – direkt online über DartCloud.`}
        />
        <link rel="canonical" href={`https://dartcloudapp.de/turnier/${slug}/anmelden`} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <BackLink slug={slug} />

        <h1 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight">Anmeldung</h1>
        <p className="mt-2 text-gray-400">{pageTitle}</p>

        {/* Turnier-Eckdaten */}
        <div className="mt-4 flex flex-wrap gap-2">
          {Array.isArray(tournament?.dates) && tournament!.dates!.length > 0 && (
            <InfoChip Icon={Calendar} label={entryService.formatShortDate(tournament!.dates![0])} />
          )}
          {typeof tournament?.entryFee === 'number' && tournament.entryFee > 0 && (
            <InfoChip Icon={Euro} label={`${tournament.entryFee.toFixed(2).replace('.', ',')} € Startgeld`} />
          )}
          {typeof tournament?.entryLimit === 'number' && tournament.entryLimit > 0 && (
            <InfoChip Icon={Users} label={`Max. ${tournament.entryLimit} Plätze`} />
          )}
          {typeof tournament?.entryDeadline === 'number' && tournament.entryDeadline > 0 && (
            <InfoChip Icon={Clock} label={`Anmeldeschluss ${entryService.formatShortDate(tournament.entryDeadline)}`} />
          )}
          {tournament?.address?.label && <InfoChip Icon={MapPin} label={tournament.address.label} />}
        </div>

        {/* Hinweis vom Veranstalter */}
        {tournament?.entryText && tournament.entryText.trim().length > 0 && (
          <div className="mt-6 p-4 rounded-xl border flex items-start gap-3" style={{ borderColor: `${ACCENT}44`, backgroundColor: `${ACCENT}11` }}>
            <Info size={20} style={{ color: ACCENT }} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">Information vom Veranstalter</p>
              <p className="text-sm text-gray-300 mt-1 whitespace-pre-line">{tournament.entryText}</p>
            </div>
          </div>
        )}

        {/* Anmeldung geschlossen */}
        {registrationClosed ? (
          <div className="mt-8 p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 bg-zinc-800">
              <Lock size={26} className="text-gray-400" />
            </div>
            <h2 className="text-xl font-bold mb-2">Anmeldung geschlossen</h2>
            <p className="text-sm text-gray-400">
              {deadlinePassed
                ? `Die Anmeldefrist für dieses Turnier ist am ${entryService.formatShortDate(tournament?.entryDeadline)} abgelaufen.`
                : 'Für dieses Turnier ist aktuell keine Online-Anmeldung möglich.'}
            </p>
          </div>
        ) : !isAuthenticated ? (
          /* Nicht eingeloggt */
          <div className="mt-8 p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${ACCENT}22` }}>
              <LogIn size={26} style={{ color: ACCENT }} />
            </div>
            <h2 className="text-xl font-bold mb-2">Anmeldung erforderlich</h2>
            <p className="text-sm text-gray-400 mb-6">
              Du musst eingeloggt sein, um dich für dieses Turnier anzumelden.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-black transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              <LogIn size={16} />
              Jetzt einloggen
            </Link>
          </div>
        ) : alreadyRegistered ? (
          /* Bereits angemeldet */
          <div className="mt-8 p-6 sm:p-8 rounded-2xl bg-zinc-900 border border-zinc-800 text-center">
            <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4 bg-emerald-500/15">
              <CheckCircle2 size={26} className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold mb-2">Bereits angemeldet</h2>
            <p className="text-sm text-gray-400 mb-6">Du bist bereits für dieses Turnier angemeldet.</p>
            <Link
              to="/meine-anmeldungen"
              className="inline-flex items-center justify-center py-3 px-6 rounded-xl font-semibold text-black transition hover:opacity-90"
              style={{ backgroundColor: ACCENT }}
            >
              Anmeldung verwalten
            </Link>
          </div>
        ) : (
          /* Formular */
          <div className="mt-8 space-y-5">
            {formError && (
              <div className="p-4 rounded-xl border border-red-500/40 bg-red-500/10 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{formError}</p>
              </div>
            )}

            {duplicateWarning && (
              <div className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/10">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-300">Mögliche Doppel-Anmeldung</p>
                    <p className="text-sm text-amber-200/80 mt-1">{duplicateWarning}</p>
                    <button
                      onClick={() => {
                        setDuplicateConfirmed(true);
                        setDuplicateWarning(null);
                      }}
                      className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition"
                    >
                      Trotzdem anmelden
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Custom-Hint */}
            {tournament?.customEntryHint && tournament.customEntryHint.trim().length > 0 && (
              <p className="text-sm text-gray-400">{tournament.customEntryHint}</p>
            )}

            {/* Teamname (nur Team-Turniere) */}
            {isTeam && (
              <FormCard title="Team">
                <FormInput
                  label="Teamname"
                  value={teamName}
                  onChange={setTeamName}
                  placeholder={tournament?.entryPlaceholder || 'Name eures Teams'}
                />
              </FormCard>
            )}

            {/* Team-Level Custom Fields */}
            {teamFields.length > 0 && (
              <FormCard title={isSingle ? 'Weitere Angaben' : 'Team-Angaben'}>
                <div className="space-y-4">
                  {teamFields.map((field) => (
                    <CustomFieldInput
                      key={field.id || field.name}
                      field={field}
                      value={teamCustomValues[field.id || '']}
                      onChange={(v) => setTeamCustomValues((prev) => ({ ...prev, [field.id || '']: v }))}
                    />
                  ))}
                </div>
              </FormCard>
            )}

            {/* Spieler-Karten */}
            {players.map((player, index) => {
              const canRemove = index > 0 && players.length > minPlayers;
              const cardTitle = isSingle
                ? 'Deine Daten'
                : index === 0
                  ? 'Deine Daten'
                  : isDouble
                    ? 'Doppelpartner'
                    : `Spieler ${index + 1}`;
              return (
                <FormCard
                  key={index}
                  title={cardTitle}
                  badge={isSingle ? undefined : `${index + 1}`}
                  onRemove={canRemove ? () => removePlayer(index) : undefined}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormInput
                      label="Vorname"
                      required
                      value={player.firstname}
                      onChange={(v) => updatePlayer(index, { firstname: v })}
                    />
                    <FormInput
                      label="Nachname"
                      required
                      value={player.lastname}
                      onChange={(v) => updatePlayer(index, { lastname: v })}
                    />
                    <FormInput
                      label="E-Mail"
                      type="email"
                      required={emailRequired}
                      value={player.email}
                      onChange={(v) => updatePlayer(index, { email: v })}
                    />
                    <FormInput
                      label="Telefon"
                      type="tel"
                      required={phoneRequired}
                      value={player.phone}
                      onChange={(v) => updatePlayer(index, { phone: v })}
                    />
                    <FormInput
                      label="Geburtsdatum"
                      type="date"
                      required={birthdayRequired}
                      value={player.birthday}
                      onChange={(v) => updatePlayer(index, { birthday: v })}
                    />
                  </div>

                  {playerFields.length > 0 && (
                    <div className="mt-5">
                      <p className="text-sm font-semibold text-gray-400 mb-3">Zusätzliche Angaben</p>
                      <div className="space-y-4">
                        {playerFields.map((field) => (
                          <CustomFieldInput
                            key={field.id || field.name}
                            field={field}
                            value={player.customValues[field.id || '']}
                            onChange={(v) => updatePlayerCustomValue(index, field.id || '', v)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </FormCard>
              );
            })}

            {/* Spieler hinzufügen */}
            {isTeam && players.length < maxPlayers && (
              <button
                onClick={addPlayer}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border font-semibold text-sm transition hover:bg-zinc-900"
                style={{ borderColor: ACCENT, color: ACCENT }}
              >
                <Plus size={16} />
                Spieler hinzufügen
              </button>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 rounded-xl font-bold text-black text-base transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: ACCENT }}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Wird gesendet…
                </>
              ) : (
                'Jetzt anmelden'
              )}
            </button>

            {tournament?.waitingEnabled === true && (
              <p className="text-xs text-gray-500 text-center">
                Ist das Turnier voll, wirst du automatisch auf die Warteliste gesetzt.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Sub-Komponenten
// =====================================================================

const BackLink: React.FC<{ slug?: string }> = ({ slug }) => (
  <Link
    to={slug ? `/turnier/${slug}` : '/turniere'}
    className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition"
  >
    <ArrowLeft size={16} />
    Zurück zum Turnier
  </Link>
);

const InfoChip: React.FC<{ Icon: React.ComponentType<any>; label: string }> = ({ Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs sm:text-sm text-gray-300">
    <Icon size={14} style={{ color: ACCENT }} />
    {label}
  </span>
);

const FormCard: React.FC<{
  title: string;
  badge?: string;
  onRemove?: () => void;
  children: React.ReactNode;
}> = ({ title, badge, onRemove, children }) => (
  <div className="p-5 sm:p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        {badge && (
          <span
            className="h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold"
            style={{ backgroundColor: `${ACCENT}22`, color: ACCENT }}
          >
            {badge}
          </span>
        )}
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          aria-label="Spieler entfernen"
          className="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-zinc-800 transition"
        >
          <X size={18} />
        </button>
      )}
    </div>
    {children}
  </div>
);

export const FormInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, required, type = 'text', placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl bg-zinc-950 border border-zinc-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#3fced1] transition [color-scheme:dark]"
    />
  </div>
);

/** Eingabe für Custom-Entry-Felder (Port des Flutter CustomEntryFieldInput). */
export const CustomFieldInput: React.FC<{
  field: CustomEntryField;
  value: any;
  onChange: (value: any) => void;
}> = ({ field, value, onChange }) => {
  const type = (field.type || 'text').toLowerCase();
  const label = field.name || 'Feld';
  const required = field.required === true;
  const options = Array.isArray(field.options) ? field.options : [];

  const labelEl = (
    <label className="block text-sm font-medium text-gray-300 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const description = field.description ? (
    <p className="text-xs text-gray-500 mt-1">{field.description}</p>
  ) : null;

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-zinc-950 border border-zinc-700 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#3fced1] transition [color-scheme:dark]';

  if (type === 'textarea') {
    return (
      <div>
        {labelEl}
        <textarea
          value={value ?? ''}
          placeholder={field.placeholder || undefined}
          maxLength={field.maxLength ?? undefined}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
        {description}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div>
        {labelEl}
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={inputClass}>
          <option value="">Bitte wählen…</option>
          {options.map((opt, idx) => {
            const optValue = opt?.value ?? opt?.label ?? '';
            return (
              <option key={opt?.id || `${optValue}-${idx}`} value={optValue || ''}>
                {opt?.label || optValue}
              </option>
            );
          })}
        </select>
        {description}
      </div>
    );
  }

  if (type === 'radio') {
    return (
      <div>
        {labelEl}
        <div className="space-y-2">
          {options.map((opt, idx) => {
            const optValue = opt?.value ?? opt?.label ?? '';
            const checked = value === optValue;
            return (
              <label
                key={opt?.id || `${optValue}-${idx}`}
                className={
                  'flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition ' +
                  (checked ? 'border-[#3fced1] bg-[#3fced1]/10' : 'border-zinc-700 bg-zinc-950 hover:border-zinc-500')
                }
              >
                <input
                  type="radio"
                  checked={checked}
                  onChange={() => onChange(optValue)}
                  className="accent-[#3fced1]"
                />
                <span className="text-sm text-gray-200">{opt?.label || optValue}</span>
              </label>
            );
          })}
        </div>
        {description}
      </div>
    );
  }

  if (type === 'checkbox') {
    const checked = value === true || value === 'true';
    return (
      <div>
        <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-zinc-700 bg-zinc-950 cursor-pointer hover:border-zinc-500 transition">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="accent-[#3fced1] h-4 w-4"
          />
          <span className="text-sm text-gray-200">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
        {description}
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div>
        {labelEl}
        <input
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          className={inputClass}
        />
        {description}
      </div>
    );
  }

  // text | number | email | phone
  const htmlType = type === 'number' ? 'number' : type === 'email' ? 'email' : type === 'phone' ? 'tel' : 'text';
  return (
    <div>
      {labelEl}
      <input
        type={htmlType}
        value={value ?? ''}
        placeholder={field.placeholder || undefined}
        maxLength={field.maxLength ?? undefined}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      {description}
    </div>
  );
};

export default TournamentRegistrationPage;
